import React from 'react';
import { cn } from '@/lib/utils';

export interface ResponsiveDataViewProps<T> {
    /** The list of data items to render on mobile */
    items: T[];
    /** Function to render a mobile card for each item */
    renderMobileCard: (item: T) => React.ReactNode;
    /** The desktop view (typically the <table> element) */
    children: React.ReactNode;
    /** Optional empty state for mobile (desktop empty state is usually built into the table) */
    emptyState?: React.ReactNode;
    /** Additional class name for the mobile container */
    mobileContainerClassName?: string;
}

/**
 * A wrapper component that switches between a desktop view (table) and a mobile view (list of cards).
 * On screen sizes >= md (768px), it shows `children`.
 * On screen sizes < md, it iterates over `items` and renders `renderMobileCard` for each.
 */
export function ResponsiveDataView<T>({
    items,
    renderMobileCard,
    children,
    emptyState,
    mobileContainerClassName
}: ResponsiveDataViewProps<T>) {
    return (
        <>
            {/* Desktop View */}
            <div className="hidden md:block w-full h-full">
                {children}
            </div>

            {/* Mobile View */}
            <div className={cn("md:hidden flex flex-col gap-3 p-3", mobileContainerClassName)}>
                {items.length === 0 ? (
                    emptyState ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center text-slate-500 text-sm">
                            {emptyState}
                        </div>
                    ) : null
                ) : (
                    items.map((item, i) => (
                        <React.Fragment key={i}>
                            {renderMobileCard(item)}
                        </React.Fragment>
                    ))
                )}
            </div>
        </>
    );
}
